import React, {
  ChangeEvent,
  KeyboardEventHandler,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'

import type { AdditionalCostType } from '@api'
import type { CellContext } from '@tanstack/react-table'

import CheckIcon from '@/assets/icons/icon-check.svg'
import { Input } from '@/components/input'
import { KeyCodes } from '@/consts/keys.ts'
import { IconButton } from '@/modules/chatting/ui/components/icon-button'

export const AccountingAdditionalCostTypeEditableCell = (
  cellContext: CellContext<AdditionalCostType, string> & {
    onSubmit: (value: string) => Promise<void>
    onBlur: () => void
  },
) => {
  const ref = useRef<HTMLInputElement>(null)
  const submitRef = useRef<HTMLButtonElement>(null)
  const initialValue = cellContext.getValue()
  const [value, setValue] = useState(initialValue)

  useEffect(() => {
    if (ref.current) {
      ref.current.focus()
    }
  }, [])

  const handleSubmit = useCallback(async () => {
    await cellContext.onSubmit(value)
  }, [cellContext, value])

  const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value)
  }, [])

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      if (submitRef.current?.contains(event.relatedTarget as Node)) {
        return
      }
      cellContext.onBlur()
    },
    [cellContext],
  )

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      if (event.key === KeyCodes.ENTER) {
        handleSubmit()
      }
    },
    [handleSubmit],
  )

  return (
    <div className="flex items-center gap-2">
      <Input
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        className="rounded-none border-x-0 border-t-0 py-0 pl-0"
        ref={ref}
      />
      <IconButton
        className="text-success-default"
        icon={CheckIcon}
        onClick={handleSubmit}
        ref={submitRef}
      />
    </div>
  )
}
